import { useMemo } from 'react';

import { TraceEvent } from '@/types';

export type WorkflowStatus = 'idle' | 'running' | 'complete';

export interface UseWorkflowStatusResult {
  status: WorkflowStatus;
  isRunning: boolean;
  isComplete: boolean;
  hasTraces: boolean;
}

/**
 * Determines workflow status by analyzing trace events.
 * Extracted from WorkflowStatusButton for reuse in TraceDrawer.
 */
export function useWorkflowStatus(
  traces: TraceEvent[] | undefined
): UseWorkflowStatusResult {
  return useMemo(() => {
    if (!traces || !Array.isArray(traces) || traces.length === 0) {
      return {
        status: 'idle' as const,
        isRunning: false,
        isComplete: false,
        hasTraces: false,
      };
    }

    // Group events by span_id to determine completion status
    const spanStatus = new Map<
      string,
      { hasStart: boolean; hasEnd: boolean }
    >();

    traces.forEach((event: TraceEvent) => {
      if (!event) return;

      const isTrackedEvent =
        event.event_type === 'agent_start' ||
        event.event_type === 'agent_end' ||
        event.event_type === 'job_start' ||
        event.event_type === 'job_end' ||
        event.event_type === 'job_queued';

      if (!isTrackedEvent) return;

      const spanId = event.span_id;
      const current = spanStatus.get(spanId) || {
        hasStart: false,
        hasEnd: false,
      };

      if (
        event.event_type === 'agent_start' ||
        event.event_type === 'job_start' ||
        event.event_type === 'job_queued'
      ) {
        current.hasStart = true;
      }

      if (event.event_type === 'agent_end' || event.event_type === 'job_end') {
        current.hasEnd = true;
      }

      spanStatus.set(spanId, current);
    });

    const hasTrackableSpans = spanStatus.size > 0;
    const allSpansComplete = Array.from(spanStatus.values()).every(
      status => !status.hasStart || status.hasEnd
    );
    const isComplete = hasTrackableSpans && allSpansComplete;

    if (!hasTrackableSpans) {
      return {
        status: 'idle' as const,
        isRunning: false,
        isComplete: false,
        hasTraces: true,
      };
    }

    return {
      status: isComplete ? ('complete' as const) : ('running' as const),
      isRunning: !isComplete,
      isComplete,
      hasTraces: true,
    };
  }, [traces]);
}
